import { Grid, SvgIcon } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';
import { alpha, makeStyles, styled, withStyles, withTheme } from '@material-ui/core/styles';
import TreeItem from '@material-ui/lab/TreeItem';
import TreeView from '@material-ui/lab/TreeView';
import { Icons, Typography } from '@nutrien/cxp-components';
import PropTypes from 'prop-types';
import React from 'react';

import { useMst } from '../../mobx-models/Root';
import type { HeirachyResult } from '../../rxdb/assetsByAreaHeirachy/assetsByAreaHierarchy.types';
import type { ComponentAreaInfo } from '../../rxdb/deficiencyList/deficiencyList.types';

const MinusSquare = () => {
  return (
    <SvgIcon fontSize="inherit" className="treeView-svgIcon">
      <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z" />
    </SvgIcon>
  );
};

const PlusSquare = () => {
  return (
    <SvgIcon fontSize="inherit" className="treeView-svgIcon">
      <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
    </SvgIcon>
  );
};

const LastNodeSquare = () => {
  return (
    <SvgIcon className="close treeView-svgIcon" fontSize="inherit">
      <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
    </SvgIcon>
  );
};

const TransitionComponent = (props: object) => {
  return (
    <div>
      <Collapse {...props} />
    </div>
  );
};

TransitionComponent.propTypes = {
  in: PropTypes.bool,
};

const StyledTreeItem = withStyles(theme => ({
  iconContainer: {
    '& .close': {
      opacity: 0.3,
    },
    '& .treeView-svgIcon': {
      width: 14,
      height: 14,
      color: 'rgba(255, 255, 255, 0.87)',
    },
  },
  root: {
    '&.MuiTreeItem-root> .MuiTreeItem-content': {
      padding: '8px',
      paddingBottom: '16px',
    },
    '&.MuiTreeItem-root.Mui-selected > .MuiTreeItem-content ': {},
    '&.MuiTreeItem-root.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label': {
      backgroundColor: 'transparent',
    },

    '&.MuiTreeItem-root > .MuiTreeItem-content:hover': {},
    '&.MuiTreeItem-root > .MuiTreeItem-content:hover .MuiTreeItem-label': {
      backgroundColor: 'transparent',
    },
  },
  group: {
    marginLeft: 24,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
}))(props => <TreeItem nodeId="" {...props} TransitionComponent={TransitionComponent} />);

interface CustomTreeViewProps {
  type?: string;
  nodeId: string;
  label: React.ReactNode;
  id?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const CustomTreeView = withTheme(
  styled((props: CustomTreeViewProps) => <StyledTreeItem {...props} />)({}),
);

const useStyles = makeStyles({
  root: {
    height: 'auto',
    msOverflowY: 'auto',
    width: '100%',
  },
  addIcon: {
    position: 'absolute',
    right: '0',
    paddingRight: '0.3rem',
  },
  treeViewGrid: {
    fontWeight: 400,
    display: 'flex',
  },
  treeViewTypography: {
    maxWidth: 280,
    display: 'inline-block',
    wordBreak: 'break-word',
    color: 'rgba(238, 238, 238, 1)',
    letterSpacing: '0.15px',
    paddingLeft: '7px',
  },
});

interface Props {
  treeViewData: HeirachyResult[];
  onChange: (val: ComponentAreaInfo[]) => void;
  tempComponentLocation: ComponentAreaInfo[];
  expanded: string[];
  onNodeToggle: (nodeId: string[]) => void;
  componentVersionId?: number | null;
}

const RenderTreeView = ({
  treeViewData,
  onChange,
  tempComponentLocation,
  expanded,
  onNodeToggle,
  componentVersionId,
}: Props) => {
  const classes = useStyles();
  const { CurrentUserSelected } = useMst();

  const onTreeItemDeleteClick = (node: HeirachyResult) => {
    onChange(tempComponentLocation.filter((val: ComponentAreaInfo) => node.id != val.areaId));
  };

  const onTreeItemClick = (node: HeirachyResult) => {
    const index = tempComponentLocation?.findIndex(
      (val: ComponentAreaInfo) => node.id === val.areaId,
    );
    if (index === -1 || !tempComponentLocation) {
      onChange([
        {
          areaId: node.id,
          areaPcsId: node.areaPcsId,
          trafficSafety: node.trafficSafety,
          inspectionId: CurrentUserSelected.inspectionId,
          componentVersionId,
        },
        ...tempComponentLocation,
      ]);
    }
  };

  const renderTreeItems = (nodes: HeirachyResult, currentLevel = 0) => {
    return (
      nodes.type !== '15' &&
      nodes.type !== '25' &&
      nodes.type !== '35' && (
        <CustomTreeView
          key={String(nodes.id) + currentLevel}
          nodeId={String(nodes.id)}
          label={
            <Grid item xs={12} className={classes.treeViewGrid}>
              <Grid xs={10}>
                <Typography fontWeight={600} className={classes.treeViewTypography}>
                  {nodes.areaPcsId}
                </Typography>
              </Grid>
              <Grid xs={2}>
                {tempComponentLocation?.findIndex(
                  (val: ComponentAreaInfo) => val?.areaId == nodes.id,
                ) == -1 ? (
                  <>
                    <Icons.PlusCircleFeather
                      data-testid={`cl-${nodes.areaPcsId}`}
                      className={classes.addIcon}
                      color="primary"
                      size="large"
                      strokeWidth="2"
                      onClick={() => onTreeItemClick(nodes)}
                    />
                  </>
                ) : (
                  <Icons.MinusCircleFeather
                    className={classes.addIcon}
                    color="error"
                    size="large"
                    strokeWidth="2"
                    onClick={() => onTreeItemDeleteClick(nodes)}
                  />
                )}
              </Grid>
            </Grid>
          }
        >
          {nodes.children &&
            nodes.children.length > 0 &&
            nodes.type !== '13' &&
            nodes.type !== '23' &&
            nodes.type !== '33' &&
            nodes.children.map(element => {
              return renderTreeItems(element, currentLevel + 1);
            })}
        </CustomTreeView>
      )
    );
  };

  const renderTree = () => {
    return treeViewData.map((element: HeirachyResult) => {
      return renderTreeItems(element, 0);
    });
  };

  return (
    <TreeView
      className={classes.root}
      defaultCollapseIcon={<MinusSquare />}
      defaultExpandIcon={<PlusSquare />}
      defaultEndIcon={<LastNodeSquare />}
      expanded={expanded}
      onNodeToggle={(event, nodeId) => onNodeToggle(nodeId)}
    >
      {treeViewData && treeViewData.length > 0 && renderTree()}
    </TreeView>
  );
};

export default RenderTreeView;
